import {useMemo} from 'react';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export type ItemTrackerBreadcrumbMode =
  | 'issue-certificate'
  | 'request'
  | 'definition-view'
  | 'definition-create'
  | 'definition-edit'
  | 'assignment-view'
  | 'assignment-create'
  | 'assignment-edit'
  | 'assignment-clone';

export interface UseItemTrackerBreadcrumbsProps {
  mode: ItemTrackerBreadcrumbMode;
  definitionId?: string | number;
  assignmentId?: string | number;
  itemDefinitionName?: string;
  assigneeName?: string;
  fromDashboard?: boolean;
}

export interface UseItemTrackerBreadcrumbsResult {
  breadcrumbs: BreadcrumbItem[];
  fallbackTo: string;
}

export const useItemTrackerBreadcrumbs = ({
  mode,
  definitionId,
  assignmentId,
  itemDefinitionName,
  assigneeName,
  fromDashboard = false,
}: UseItemTrackerBreadcrumbsProps): UseItemTrackerBreadcrumbsResult => {
  return useMemo(() => {
    const crumbs: BreadcrumbItem[] = [];
    let fallbackTo = PathUtility.ItemTrackerDashboard();

    // When coming from the dashboard (user view), use dashboard-style breadcrumbs
    if (fromDashboard && mode === 'assignment-view') {
      fallbackTo = PathUtility.ItemTrackerDashboard();
      crumbs.push({label: 'Item Tracker', to: PathUtility.ItemTrackerDashboard()});
      const title = assigneeName
        ? `${itemDefinitionName || 'Item'} (${assigneeName})`
        : itemDefinitionName || 'Item';
      crumbs.push({label: title});
      return {breadcrumbs: crumbs, fallbackTo};
    }

    switch (mode) {
      case 'issue-certificate': {
        fallbackTo =
          definitionId && assignmentId
            ? PathUtility.ItemAssignmentView(String(definitionId), String(assignmentId))
            : PathUtility.ItemTrackerDashboard();
        crumbs.push({label: 'Item Tracker', to: PathUtility.ItemTrackerDashboard()});
        crumbs.push({label: itemDefinitionName || 'Item'});
        crumbs.push({label: 'Issuance Certificate'});
        break;
      }

      case 'request': {
        fallbackTo = PathUtility.ItemTrackerDashboard();
        crumbs.push({label: 'Item Tracker', to: PathUtility.ItemTrackerDashboard()});
        crumbs.push({label: 'Request Items'});
        break;
      }

      case 'definition-view': {
        fallbackTo = PathUtility.ItemDefinitions();
        crumbs.push({label: 'Item Definitions', to: PathUtility.ItemDefinitions()});
        crumbs.push({label: itemDefinitionName || 'Item'});
        break;
      }

      case 'definition-create': {
        fallbackTo = PathUtility.ItemDefinitions();
        crumbs.push({label: 'Item Definitions', to: PathUtility.ItemDefinitions()});
        crumbs.push({label: 'New Item'});
        break;
      }

      case 'definition-edit': {
        fallbackTo = PathUtility.ItemDefinitionView(definitionId!, 'content');
        crumbs.push({label: 'Item Definitions', to: PathUtility.ItemDefinitions()});
        crumbs.push({
          label: itemDefinitionName || 'Item',
          to: PathUtility.ItemDefinitionView(definitionId!, 'content'),
        });
        crumbs.push({label: 'Edit'});
        break;
      }

      case 'assignment-view': {
        fallbackTo = PathUtility.ItemDefinitionView(definitionId!, 'content');
        crumbs.push({label: 'Item Definitions', to: PathUtility.ItemDefinitions()});
        crumbs.push({
          label: itemDefinitionName || 'Item',
          to: PathUtility.ItemDefinitionView(definitionId!, 'content'),
        });
        const title = assigneeName
          ? `${itemDefinitionName || 'Item'} (${assigneeName})`
          : 'Assignment';
        crumbs.push({label: title});
        break;
      }

      case 'assignment-create': {
        fallbackTo = PathUtility.ItemDefinitionView(definitionId!, 'content');
        crumbs.push({label: 'Item Definitions', to: PathUtility.ItemDefinitions()});
        crumbs.push({
          label: itemDefinitionName || 'Item',
          to: PathUtility.ItemDefinitionView(definitionId!, 'content'),
        });
        crumbs.push({label: 'New Assignment'});
        break;
      }

      case 'assignment-edit': {
        fallbackTo = PathUtility.ItemAssignmentView(String(definitionId), String(assignmentId));
        crumbs.push({label: 'Item Definitions', to: PathUtility.ItemDefinitions()});
        crumbs.push({
          label: itemDefinitionName || 'Item',
          to: PathUtility.ItemDefinitionView(definitionId!, 'content'),
        });
        crumbs.push({
          label: assigneeName || 'Assignment',
          to: PathUtility.ItemAssignmentView(String(definitionId), String(assignmentId)),
        });
        crumbs.push({label: 'Edit'});
        break;
      }

      case 'assignment-clone': {
        fallbackTo = PathUtility.ItemAssignmentView(String(definitionId), String(assignmentId));
        crumbs.push({label: 'Item Definitions', to: PathUtility.ItemDefinitions()});
        crumbs.push({
          label: itemDefinitionName || 'Item',
          to: PathUtility.ItemDefinitionView(definitionId!, 'content'),
        });
        crumbs.push({
          label: assigneeName || 'Assignment',
          to: PathUtility.ItemAssignmentView(String(definitionId), String(assignmentId)),
        });
        crumbs.push({label: 'Clone'});
        break;
      }
    }

    return {breadcrumbs: crumbs, fallbackTo};
  }, [mode, definitionId, assignmentId, itemDefinitionName, assigneeName, fromDashboard]);
};
